/**
 * Created by fers on 05.02.16.
 */
import AppPanel from '../fw/AppPanel.react';
import React from 'react';
import PropTypes from 'prop-types';
import types from '../../common/model-api/types';
import Relay from 'react-relay';
import config from '../../common/config';

import EditorWithSaver from '../fw/EditorWithSaver.react';
import {injectIntl, defineMessages, FormattedMessage, IntlShape} from 'react-intl';
import {wrapNotFound} from '../app/NotFoundHandler.react';
import {getLastOrganisationId} from '../app/LastOrganisationId';
import * as BrowserPermission from '../BrowserPermission';
import {PERM_EDIT_INVITATION} from '../../common/util/Permission';
import CreateBulkInvitationMutation from './CreateBulkInvitationMutation';
import {
    countBulkInvitationResults,
    getEligibleOrganisations,
    toggleOrganisationSelection,
} from './bulkInvitationUI.ts';

import './Invitations.scss';
import { User } from '../../common/types/types';

const messages = defineMessages({
    title: {id: 'invitationAdd.title', 'defaultMessage': 'Invite new user'},
    sent: {id: 'invitationAdd.sent', defaultMessage: 'Sent'},
    sending: {id: 'invitationAdd.sending', defaultMessage: 'Sending'},
    sendInvitation: {id: 'invitationAdd.sendInvitation', defaultMessage: 'Send Invitation'},
    inviteToMore: {id: 'invitationAdd.inviteToMore', defaultMessage: 'Invite user to more organisations'},
    selectAll: {id: 'invitationAdd.selectAll', defaultMessage: 'Select all'},
    selectNone: {id: 'invitationAdd.selectNone', defaultMessage: 'Select none'},
    userAdded: {id: 'invitationAdd.userAdded', defaultMessage: 'The user was found and added directly without requiring invitation confirmation. No invitation email was sent.'},
    invitationSent: {id: 'invitationAdd.invitationSent', defaultMessage: 'The invitation was sent to {email}.'},
    invitationEmailFailed: {id: 'invitationAdd.invitationEmailFailed', defaultMessage: 'Invitations were created, but the email could not be sent. You can submit the same request again safely.'},
    resultCounts: {id: 'invitationAdd.resultCounts', defaultMessage: 'Added: {added}, already members: {alreadyMember}, invited: {invited}, failed: {failed}.'},
});

interface InvitationAddProps {
 intl: IntlShape;
 user: User;
}

class InvitationAdd extends React.Component<InvitationAddProps> {

    static propTypes = {
        intl: PropTypes.object.isRequired,
        user: PropTypes.object.isRequired,
    };

    static contextTypes = {
        router: PropTypes.object
    };

    constructor(props) {
        super(props);

        this.state = {
            inviteToMore: false,
            selectedOrganisationIds: [],
            result: null,
        };
    }

    typeName = 'invitations';

    getEligibleOrganisations() {
        const currentOrganisationId = this.props.user.organisation.realId;
        const edges = this.props.user.accessibleOrganisations ? this.props.user.accessibleOrganisations.edges : [];
        return getEligibleOrganisations(
            edges.map((edge) => edge.node),
            currentOrganisationId,
            (organisationId) => BrowserPermission.hasOrganisationPermission(PERM_EDIT_INVITATION, organisationId),
        );
    }

    setInviteToMore(inviteToMore) {
        this.setState({
            inviteToMore,
            selectedOrganisationIds: inviteToMore ? this.state.selectedOrganisationIds : [],
        });
    }

    toggleOrganisation(organisationId) {
        const selectedOrganisationIds = toggleOrganisationSelection(this.state.selectedOrganisationIds, organisationId);
        this.setState({selectedOrganisationIds});
    }

    getSaver() {
        return {
            // eslint-disable-next-line max-params
            save: (data, _defaultValue, _columns, onSuccess, onFailure) => new Promise((resolve) => {
                const organisationIds = this.state.inviteToMore ? this.state.selectedOrganisationIds : [];
                Relay.Store.commitUpdate(new CreateBulkInvitationMutation({
                    currentOrganisationId: this.props.user.organisation.realId,
                    email: data.email,
                    organisationIds,
                    role: data.role,
                }), {
                    onFailure: (transaction) => {
                        onFailure(transaction);
                        resolve(null);
                    },
                    onSuccess: (response) => {
                        onSuccess(response);
                        resolve(response);
                    },
                });
            }),
        };
    }

    onSuccess(response) {
        this.setState({
            result: response.createBulkInvitation,
        });
    }

    renderResult() {
        const result = this.state.result;
        if (!result) {
            return null;
        }
        const counts = countBulkInvitationResults(result.results || []);
        const style = result.userFound || result.emailSent ? 'info' : 'warning';

        return (
            <div className={`alert alert-${style}`} role="alert">
                <p>
                    {result.userFound
                        ? this.props.intl.formatMessage(messages.userAdded)
                        : result.emailSent
                            ? this.props.intl.formatMessage(messages.invitationSent, {email: result.email})
                            : this.props.intl.formatMessage(messages.invitationEmailFailed)}
                </p>
                <p>{this.props.intl.formatMessage(messages.resultCounts, {
                    added: counts.added || 0,
                    alreadyMember: counts.already_member || 0,
                    invited: counts.invited || 0,
                    failed: counts.failed || 0,
                })}</p>
                {(result.results || []).filter((item) => item.error).map((item) =>
                    <div key={item.organisationId}>{item.organisationName}: {item.error}</div>
                )}
            </div>
        );
    }

    render() {
        let type = types[this.typeName];
        const eligibleOrganisations = this.getEligibleOrganisations();

        return (
            <AppPanel breadcrumbParams={[
                {type: types['organisations'], data: this.props.user.organisation}
            ]} className="invitationScreen" title={this.props.intl.formatMessage(messages.title)}>
                <p><FormattedMessage defaultMessage="By inviting user you will allow access to your integrations in {productName}." id="invitationAdd.explanationWithoutOrg" values={{productName: config.PRODUCT_NAME}}/></p>

                {this.renderResult()}

                <EditorWithSaver
                    columns={['email', 'role']}
                    enableDisable
                    isCreate
                    metadata={type}
                    onSuccess={this.onSuccess.bind(this)}
                    organisationId={this.props.user.organisation.realId}
                    parent={this.props.user.organisation}
                    saver={() => this.getSaver()}
                    submitLoadingButtonNewConfig={{
                        doneText: this.props.intl.formatMessage(messages.sent),
                        icon: 'fa-envelope-o',
                        progressText: this.props.intl.formatMessage(messages.sending),
                        text: this.props.intl.formatMessage(messages.sendInvitation),
                    }}
                >
                    {eligibleOrganisations.length > 0 ? (
                        <div className="invitationOrganisationSelector">
                            <label>
                                <input
                                    checked={this.state.inviteToMore}
                                    onChange={(event) => this.setInviteToMore(event.target.checked)}
                                    type="checkbox"
                                />{' '}
                                {this.props.intl.formatMessage(messages.inviteToMore)}
                            </label>
                            {this.state.inviteToMore ? (
                                <div className="invitationOrganisationSelector__list">
                                    <div>
                                        <button className="btn btn-link" onClick={() => this.setState({selectedOrganisationIds: eligibleOrganisations.map((organisation) => organisation.realId)})} type="button">
                                            {this.props.intl.formatMessage(messages.selectAll)}
                                        </button>
                                        <button className="btn btn-link" onClick={() => this.setState({selectedOrganisationIds: []})} type="button">
                                            {this.props.intl.formatMessage(messages.selectNone)}
                                        </button>
                                    </div>
                                    {eligibleOrganisations.map((organisation) => (
                                        <label className="invitationOrganisationSelector__item" key={organisation.realId}>
                                            <input
                                                checked={this.state.selectedOrganisationIds.includes(organisation.realId)}
                                                onChange={() => this.toggleOrganisation(organisation.realId)}
                                                type="checkbox"
                                            />{' '}
                                            {organisation.name}
                                        </label>
                                    ))}
                                </div>
                            ) : null}
                        </div>
                    ) : null}
                </EditorWithSaver>
            </AppPanel>
        );
    }
}


export default Relay.createContainer(wrapNotFound(injectIntl(InvitationAdd), (props) => {
    return props.user.organisation;
}), {
    initialVariables: {
        organisationId: '',
    },
    prepareVariables (variables) {
        if (!variables.organisationId) {
            variables.organisationId = getLastOrganisationId();
        }

        return variables;
    },
    fragments: {
        user: () => Relay.QL`
            fragment on User {
                organisation(id: $organisationId) {
                    id,
                    realId,
                    name,
                },
                accessibleOrganisations(first: 1000) {
                    edges {
                        node {
                            id,
                            realId,
                            name,
                        }
                    }
                }
            }
        `,
    },
});
